import axios from 'axios'

import { clearMessages } from 'redux-flash'

import { pollIsAlreadyFilledLocalStoragePrefix } from '../options'
import { writeStorage } from '../storage'
import { findMissingQuestionIds, findLowMarkGroups, findLowMarkTrainerIds, lowMarkThreshold } from './validation'

import t from '../translations'

const SET_POLL_ANSWER = 'answers/SET_POLL_ANSWER'
const SAVE_POLL_ANSWERS_START = 'answers/SAVE_POLL_ANSWERS_START'
const SAVE_POLL_ANSWERS_STOP = 'answers/SAVE_POLL_ANSWERS_STOP'
const SAVED_POLL_ANSWERS_CORRECTLY = 'answers/SAVED_POLL_ANSWERS_CORRECTLY'
const SAVED_POLL_ANSWERS_WITH_ERRORS = 'answers/SAVED_POLL_ANSWERS_WITH_ERRORS'
const SET_ANSWER_SET = 'answers/SET_ANSWER_SET'
const MARK_ANSWERS_SAVED = 'answers/MARK_ANSWERS_SAVED'
const SET_VALIDATION = 'answers/SET_VALIDATION'
const CLEAR_VALIDATION = 'answers/CLEAR_VALIDATION'
const CLEAR_SAVE_ERROR = 'answers/CLEAR_SAVE_ERROR'

// Puste pole tekstowe (także same spacje) i pytanie ze skalą bez zaznaczenia to „brak odpowiedzi”.
const isAnswered = (question, answer) => (
  question.type === 'TEXT' ?
    typeof answer === 'string' && answer.trim() !== ''
    :
    answer !== undefined
)

// Przycisk „OK” (po potwierdzeniu w oknie): uzupełnia tylko pytania bez odpowiedzi — zaznaczone oceny i wpisane
// teksty zostają. Typy bez zmian: ścieżka „OK” wysyła liczby 5 i 10, odpowiedzi zaznaczone ręcznie są tekstem.
// Pola tekstowe przy ręcznie wystawionej niskiej ocenie zostają puste: tekst zastępczy spełniałby regułę
// uzasadnienia, więc walidacja poprosi kursanta o własne uzasadnienie.
export const okButton = () => (dispatch, getState) => {
  const pollData = getState().poll.pollData
  const currentAnswers = getState().saveAnswers.answers
  const lowMarkTrainerIds = findLowMarkTrainerIds(pollData.questions, currentAnswers, lowMarkThreshold())
  pollData.questions.forEach(question => {
    if (isAnswered(question, currentAnswers[question.questionId])) return
    if (question.type === 'TEXT' && lowMarkTrainerIds.includes(question.trainer)) return

    let answer = null
    switch (question.type) {
      case 'RANGE':
        answer = 5
        break
      case 'NPS':
        answer = 10
        break
      case 'TEXT':
        answer = t.pollView.okBtnTxtAnswer
        break
      default:
        break
    }

    dispatch(setAnswer(question.questionId, answer, null))
  })

  dispatch(saveAnswers())
}

export const setAnswer = (questionId, answer, trainerUid) => ({
  type: SET_POLL_ANSWER,
  payload: { questionId, answer, trainerUid },
})

// Jeden zestaw odpowiedzi na sesję strony. Ponowienie po błędzie zapisu używa tego samego answerSetUid i znacznika
// czasu: zdarzenie answers__pl.create (isa-surveys-init/data/events.js) dopisuje UID do poll.answerSets tylko raz,
// więc panel liczy jednego kursanta, a odpowiedzi z obu prób składają się na jeden kompletny zestaw.
const getAnswerSet = (dispatch, getState, pollUid) => {
  const existing = getState().saveAnswers.answerSet
  if (existing) return existing

  const timestamp = Date.now()
  const answerSet = {
    uid: `${pollUid}-${timestamp}-${Math.random().toString(36).substr(2, 9)}`,
    timestamp,
  }
  dispatch({ type: SET_ANSWER_SET, payload: { answerSet } })
  return answerSet
}

export const saveAnswers = () => (dispatch, getState) => {
  // Komunikat o błędzie poprzedniego zapisu znika przy kolejnej próbie wysłania.
  dispatch({ type: CLEAR_SAVE_ERROR })

  const pollData = getState().poll.pollData
  const pollUid = pollData.pollUid
  const currentAnswers = getState().saveAnswers.answers

  // Walidacja jak dotąd: najpierw brakujące odpowiedzi, potem niska ocena bez uzasadnienia. Zamiast znikającego
  // po 5 s komunikatu w rogu (na telefonie zasłaniał przycisk) wynik trafia do stanu: błędy przy pytaniach,
  // podsumowanie nad przyciskiem wysyłania, fokus na pierwszym pytaniu do poprawy (Poll.js).
  const missingQuestionIds = findMissingQuestionIds(pollData.questions, currentAnswers)
  if (missingQuestionIds.length > 0) {
    dispatch({ type: SET_VALIDATION, payload: { missingQuestionIds, lowMarkGroups: [] } })
    return
  }

  const lowMarkGroups = findLowMarkGroups(pollData.questions, currentAnswers, lowMarkThreshold())
  if (lowMarkGroups.length > 0) {
    dispatch({ type: SET_VALIDATION, payload: { missingQuestionIds: [], lowMarkGroups } })
    return
  }

  dispatch({ type: CLEAR_VALIDATION })

  const { uid: answerSetUid, timestamp } = getAnswerSet(dispatch, getState, pollUid)
  const savedQuestionUids = getState().saveAnswers.savedQuestionUids

  // Ponowienie wysyła tylko odpowiedzi, których serwer jeszcze nie przyjął — bez duplikatów w bazie.
  const requests = Object.entries(currentAnswers)
    .filter(([questionUid]) => !savedQuestionUids[questionUid])
    .map(([questionUid, answer]) => {
      const question = pollData.questions.filter(question => question.questionId === questionUid)[0]
      return {
        questionUid,
        body: {
          questionUid,
          answer,
          timestamp,
          answerSetUid,
          pollUid,
          trainerUid: question.trainer || null,
          owner: question.owner || null,
          questionType: question.type,
        },
      }
    })

  // Żaden stary komunikat redux-flash nie może zostać obok ekranu „Poprawnie zapisano” ani się piętrzyć.
  dispatch(clearMessages())
  dispatch({ type: SAVE_POLL_ANSWERS_START })

  // Każdy POST rozliczany osobno (Promise.all odrzucał całość po pierwszym błędzie): wiemy, co serwer przyjął.
  return Promise.all(
    requests.map(({ questionUid, body }) => (
      axios.post(`${window._env_.REACT_APP_API_URL}/actions/answers`, body)
        .then(() => ({ questionUid, saved: true }), (error) => ({ questionUid, saved: false, error }))
    ))
  )
    .then((results) => {
      const savedNow = results.filter(result => result.saved).map(result => result.questionUid)
      const failed = results.filter(result => !result.saved)

      dispatch({ type: MARK_ANSWERS_SAVED, payload: { questionUids: savedNow } })
      dispatch({ type: SAVE_POLL_ANSWERS_STOP })

      // Błąd zapisu pokazuje SaveErrorMessage nad przyciskiem wysyłania (nie toast w rogu: na telefonie zasłaniał
      // przycisk w 100% i kolejne dotknięcie „Wyślij ankietę” trafiało w komunikat).
      if (failed.length > 0) {
        dispatch({ type: SAVED_POLL_ANSWERS_WITH_ERRORS, payload: { error: failed[0].error } })
        return
      }

      // Wszystkie odpowiedzi są w bazie. Znacznik „już wypełniona” to tylko wygoda: gdy localStorage go nie przyjmie
      // (brak miejsca, blokada), nie wolno pokazać błędu zapisu — kursant wysłałby ankietę drugi raz.
      writeStorage(`${pollIsAlreadyFilledLocalStoragePrefix}${pollUid}`, Date.now())
      dispatch({ type: SAVED_POLL_ANSWERS_CORRECTLY })
    })
}

const initialState = {
  answers: {},
  answersAreSaving: false,
  answersAreSaved: false,
  // { uid, timestamp } — tworzony przy pierwszej próbie zapisu, potem stały do końca sesji strony.
  answerSet: null,
  // questionUid → true dla odpowiedzi, które serwer już przyjął (200).
  savedQuestionUids: {},
  // Wynik ostatniej nieudanej walidacji: { attempt, missingQuestionIds, lowMarkGroups } albo null.
  // attempt rośnie przy każdej próbie, żeby fokus wracał do pierwszego pytania także przy powtórnym kliknięciu.
  validation: null,
  // Ostatnia próba zapisu nie powiodła się (część odpowiedzi nie trafiła do bazy); czyści to kolejna próba.
  saveError: false,
}

export default (state = initialState, action) => {
  switch (action.type) {
    case SAVE_POLL_ANSWERS_START:
      return {
        ...state,
        answersAreSaving: true,
      }
    case SAVE_POLL_ANSWERS_STOP:
      return {
        ...state,
        answersAreSaving: false,
      }
    case SAVED_POLL_ANSWERS_CORRECTLY:
      return {
        ...state,
        answersAreSaved: true,
      }
    case SAVED_POLL_ANSWERS_WITH_ERRORS:
      return {
        ...state,
        saveError: true,
      }
    case CLEAR_SAVE_ERROR:
      return {
        ...state,
        saveError: false,
      }
    case SET_VALIDATION:
      return {
        ...state,
        validation: {
          attempt: (state.validation ? state.validation.attempt : 0) + 1,
          missingQuestionIds: action.payload.missingQuestionIds,
          lowMarkGroups: action.payload.lowMarkGroups,
        },
      }
    case CLEAR_VALIDATION:
      return {
        ...state,
        validation: null,
      }
    case SET_ANSWER_SET:
      return {
        ...state,
        answerSet: action.payload.answerSet,
      }
    case MARK_ANSWERS_SAVED:
      return {
        ...state,
        savedQuestionUids: action.payload.questionUids.reduce(
          (saved, questionUid) => ({ ...saved, [questionUid]: true }),
          state.savedQuestionUids
        ),
      }
    case SET_POLL_ANSWER:
      // Odpowiedź przyjętą już przez serwer (częściowy zapis) zamrażamy: ekran i walidacja mają pokazywać to, co jest
      // w bazie. Pytanie jest też zablokowane w interfejsie; ten warunek chroni przed każdą inną drogą zmiany.
      if (state.savedQuestionUids[action.payload.questionId]) return state
      let newAnswer = {}
      newAnswer[action.payload.questionId] = action.payload.answer
      return {
        ...state,
        answers: {
          ...state.answers,
          ...newAnswer,
        },
      }
    default:
      return state
  }
}